import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import { Save, Pencil, Trash2, Plus, GripVertical } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Project {
  _id: string;
  name: string;
}

interface IssueLocation {
  id: string;
  name: string;
  isSuiteUnit: boolean;
  isCommonArea: boolean;
  order: number;
}

interface ListItem {
  id: string;
  name: string;
  isSuiteUnit?: boolean;
  isCommonArea?: boolean;
  construction?: boolean;
  inspector?: boolean;
  manager?: boolean;
  trade?: boolean;
  showOnPurchaserPortal?: boolean;
  order: number;
}

interface User {
  contactId: string;
  name: string;
  email: string;
  construction: boolean;
  inspector: boolean;
  manager: boolean;
  trade: boolean;
  tradeIds: string[];
}

interface Contact {
  _id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  emails: Array<{ email: string; isPrimary: boolean }>;
}

const ServiceSettings: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState('roles');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  // Locations state
  const [locations, setLocations] = useState<IssueLocation[]>([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<IssueLocation | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);
  const [locationsPage, setLocationsPage] = useState(1);
  const locationsPerPage = 10;

  // Drag & drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Tags state
  const [tags, setTags] = useState<ListItem[]>([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState<ListItem | null>(null);
  const [showDeleteTagConfirm, setShowDeleteTagConfirm] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const [tagsPage, setTagsPage] = useState(1);
  const tagsPerPage = 10;

  // Deficiency Types state
  const [deficiencyTypes, setDeficiencyTypes] = useState<ListItem[]>([]);
  const [showDeficiencyTypeModal, setShowDeficiencyTypeModal] = useState(false);
  const [editingDeficiencyType, setEditingDeficiencyType] = useState<ListItem | null>(null);
  const [showDeleteDeficiencyTypeConfirm, setShowDeleteDeficiencyTypeConfirm] = useState(false);
  const [deficiencyTypeToDelete, setDeficiencyTypeToDelete] = useState<string | null>(null);
  const [deficiencyTypesPage, setDeficiencyTypesPage] = useState(1);
  const deficiencyTypesPerPage = 10;

  // Priorities state
  const [priorities, setPriorities] = useState<ListItem[]>([]);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [editingPriority, setEditingPriority] = useState<ListItem | null>(null);
  const [showDeletePriorityConfirm, setShowDeletePriorityConfirm] = useState(false);
  const [priorityToDelete, setPriorityToDelete] = useState<string | null>(null);

  // Trades state
  const [trades, setTrades] = useState<ListItem[]>([]);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [editingTrade, setEditingTrade] = useState<ListItem | null>(null);
  const [showDeleteTradeConfirm, setShowDeleteTradeConfirm] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState<string | null>(null);
  const [tradesPage, setTradesPage] = useState(1);
  const tradesPerPage = 10;

  // Inspection Types state
  const [inspectionTypes, setInspectionTypes] = useState<ListItem[]>([]);
  const [showInspectionTypeModal, setShowInspectionTypeModal] = useState(false);
  const [editingInspectionType, setEditingInspectionType] = useState<ListItem | null>(null);
  const [showDeleteInspectionTypeConfirm, setShowDeleteInspectionTypeConfirm] = useState(false);
  const [inspectionTypeToDelete, setInspectionTypeToDelete] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [usersPage, setUsersPage] = useState(1);
  const usersPerPage = 10;
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsSearch, setContactsSearch] = useState('');
  const [contactsPagination, setContactsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Settings state
  const [settings, setSettings] = useState({
    roles: {
      construction: true,
      inspector: true,
      manager: true,
      trade: true
    },
    emailNotifications: {
      construction: {
        frequency: 'daily' as 'none' | 'daily' | 'weekly',
        time: '00:00',
        days: { sunday: false, monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false },
        ccEmails: '',
        notes: ''
      },
      inspector: {
        frequency: 'none' as 'none' | 'daily' | 'weekly',
        time: '00:00',
        days: { sunday: false, monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false },
        ccEmails: '',
        notes: ''
      },
      manager: {
        frequency: 'none' as 'none' | 'daily' | 'weekly',
        time: '00:00',
        days: { sunday: false, monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false },
        ccEmails: '',
        notes: ''
      },
      trade: {
        frequency: 'none' as 'none' | 'daily' | 'weekly',
        time: '00:00',
        days: { sunday: false, monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false },
        ccEmails: '',
        notes: ''
      }
    }
  });

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (selectedProjectId) {
      loadSettings(selectedProjectId);
      loadLocations(selectedProjectId);
      loadTags(selectedProjectId);
      loadDeficiencyTypes(selectedProjectId);
      loadPriorities(selectedProjectId);
      loadTrades(selectedProjectId);
      loadInspectionTypes(selectedProjectId);
      loadUsers(selectedProjectId);
    }
  }, [selectedProjectId]);

  // Debounce contact search
  useEffect(() => {
    if (!showAddUsersModal) return;

    const timeoutId = setTimeout(() => {
      loadContacts(contactsSearch, contactsPagination.page);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [contactsSearch, contactsPagination.page, showAddUsersModal]);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project
      if (projectsList.length > 0 && !selectedProjectId) {
        setSelectedProjectId(projectsList[0]._id);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  const loadSettings = async (projectId: string) => {
    if (!workspaceId) return;

    setLoading(true);
    try {
      const response = await api.get(`/service-settings/${projectId}`);
      const loadedSettings = response.data.settings;

      // Merge loaded settings with default structure to ensure all properties exist
      setSettings(prev => ({
        roles: {
          construction: loadedSettings?.roles?.construction ?? prev.roles.construction,
          inspector: loadedSettings?.roles?.inspector ?? prev.roles.inspector,
          manager: loadedSettings?.roles?.manager ?? prev.roles.manager,
          trade: loadedSettings?.roles?.trade ?? prev.roles.trade
        },
        emailNotifications: {
          construction: loadedSettings?.emailNotifications?.construction ?? prev.emailNotifications.construction,
          inspector: loadedSettings?.emailNotifications?.inspector ?? prev.emailNotifications.inspector,
          manager: loadedSettings?.emailNotifications?.manager ?? prev.emailNotifications.manager,
          trade: loadedSettings?.emailNotifications?.trade ?? prev.emailNotifications.trade
        }
      }));
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading settings:', err);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const loadLocations = async (projectId: string) => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/service-settings/${projectId}/locations`);
      const locationsData = response.data.locations.map((loc: any) => ({
        id: loc._id,
        name: loc.name,
        isSuiteUnit: loc.isSuiteUnit,
        isCommonArea: loc.isCommonArea,
        order: loc.order
      }));
      setLocations(locationsData);
    } catch (err: any) {
      console.error('Error loading locations:', err);
      toast.error('Failed to load locations');
    }
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setShowLocationModal(true);
  };

  const handleEditLocation = (location: IssueLocation) => {
    setEditingLocation(location);
    setShowLocationModal(true);
  };

  const handleDeleteLocation = (locationId: string) => {
    setLocationToDelete(locationId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteLocation = async () => {
    if (!locationToDelete) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/locations/${locationToDelete}`);
      setLocations(prev => prev.filter(loc => loc.id !== locationToDelete));
      toast.success('Location deleted successfully');
    } catch (err: any) {
      console.error('Error deleting location:', err);
      toast.error('Failed to delete location');
    } finally {
      setLocationToDelete(null);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder locations array
    const newLocations = [...locations];
    const [draggedLocation] = newLocations.splice(draggedIndex, 1);
    newLocations.splice(dropIndex, 0, draggedLocation);

    // Update order numbers
    const updatedLocations = newLocations.map((loc, index) => ({
      ...loc,
      order: index + 1
    }));

    // Update local state immediately for better UX
    setLocations(updatedLocations);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Update backend
    try {
      await api.put(`/service-settings/${selectedProjectId}/locations/reorder`, {
        locations: updatedLocations.map(loc => ({
          _id: loc.id,
          name: loc.name,
          isSuiteUnit: loc.isSuiteUnit,
          isCommonArea: loc.isCommonArea,
          order: loc.order
        }))
      });
      toast.success('Location order updated');
    } catch (err: any) {
      console.error('Error updating location order:', err);
      toast.error('Failed to update location order');
      // Revert on error by refetching
      loadLocations(selectedProjectId);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSaveLocation = async (locationData: Omit<IssueLocation, 'id' | 'order'>) => {
    try {
      if (editingLocation) {
        // Update existing location
        const response = await api.put(`/service-settings/${selectedProjectId}/locations/${editingLocation.id}`, locationData);

        setLocations(prev => prev.map(loc =>
          loc.id === editingLocation.id
            ? { id: response.data.location._id, ...response.data.location }
            : loc
        ));
        toast.success('Location updated successfully');
      } else {
        // Add new location
        const response = await api.post(`/service-settings/${selectedProjectId}/locations`, locationData);

        const newLocation: IssueLocation = {
          id: response.data.location._id,
          name: response.data.location.name,
          isSuiteUnit: response.data.location.isSuiteUnit,
          isCommonArea: response.data.location.isCommonArea,
          order: response.data.location.order
        };
        setLocations(prev => [...prev, newLocation]);
        toast.success('Location added successfully');
      }

      setShowLocationModal(false);
      setEditingLocation(null);
    } catch (err: any) {
      console.error('Error saving location:', err);
      toast.error('Failed to save location');
    }
  };

  // Tags handlers
  const loadTags = async (projectId: string) => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/service-settings/${projectId}/tags`);
      const tagsData = response.data.items.map((item: any) => ({
        id: item._id,
        name: item.name,
        isSuiteUnit: item.isSuiteUnit || false,
        isCommonArea: item.isCommonArea || false,
        order: item.order
      }));
      setTags(tagsData);
    } catch (err: any) {
      console.error('Error loading tags:', err);
      toast.error('Failed to load tags');
    }
  };

  const handleAddTag = () => {
    setEditingTag(null);
    setShowTagModal(true);
  };

  const handleEditTag = (tag: ListItem) => {
    setEditingTag(tag);
    setShowTagModal(true);
  };

  const handleDeleteTag = (tagId: string) => {
    setTagToDelete(tagId);
    setShowDeleteTagConfirm(true);
  };

  const confirmDeleteTag = async () => {
    if (!tagToDelete) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/tags/${tagToDelete}`);
      setTags(prev => prev.filter(tag => tag.id !== tagToDelete));
      toast.success('Tag deleted successfully');
    } catch (err: any) {
      console.error('Error deleting tag:', err);
      toast.error('Failed to delete tag');
    } finally {
      setTagToDelete(null);
    }
  };

  const handleDragEndTags = async (draggedIdx: number, dropIdx: number) => {
    if (draggedIdx === dropIdx) return;

    const newTags = [...tags];
    const [draggedTag] = newTags.splice(draggedIdx, 1);
    newTags.splice(dropIdx, 0, draggedTag);

    const updatedTags = newTags.map((tag, index) => ({
      ...tag,
      order: index + 1
    }));

    setTags(updatedTags);

    try {
      await api.put(`/service-settings/${selectedProjectId}/tags/reorder`, {
        items: updatedTags.map(tag => ({
          _id: tag.id,
          name: tag.name,
          isSuiteUnit: tag.isSuiteUnit,
          isCommonArea: tag.isCommonArea,
          order: tag.order
        }))
      });
      toast.success('Tag order updated');
    } catch (err: any) {
      console.error('Error updating tag order:', err);
      toast.error('Failed to update tag order');
      loadTags(selectedProjectId);
    }
  };

  const handleSaveTag = async (tagData: { name: string; isSuiteUnit: boolean; isCommonArea: boolean }) => {
    try {
      if (editingTag) {
        const response = await api.put(`/service-settings/${selectedProjectId}/tags/${editingTag.id}`, tagData);
        setTags(prev => prev.map(tag =>
          tag.id === editingTag.id
            ? {
                id: response.data.item._id,
                name: response.data.item.name,
                isSuiteUnit: response.data.item.isSuiteUnit,
                isCommonArea: response.data.item.isCommonArea,
                order: response.data.item.order
              }
            : tag
        ));
        toast.success('Tag updated successfully');
      } else {
        const response = await api.post(`/service-settings/${selectedProjectId}/tags`, {
          ...tagData,
          order: tags.length
        });
        const newTag: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          isSuiteUnit: response.data.item.isSuiteUnit,
          isCommonArea: response.data.item.isCommonArea,
          order: response.data.item.order
        };
        setTags(prev => [...prev, newTag]);
        toast.success('Tag added successfully');
      }

      setShowTagModal(false);
      setEditingTag(null);
    } catch (err: any) {
      console.error('Error saving tag:', err);
      toast.error('Failed to save tag');
    }
  };

  // Deficiency Types handlers
  const loadDeficiencyTypes = async (projectId: string) => {
    try {
      const response = await api.get(`/service-settings/${projectId}/deficiency-types`);
      const deficiencyTypesData = response.data.items.map((item: any) => ({
        id: item._id,
        name: item.name,
        isSuiteUnit: item.isSuiteUnit || false,
        isCommonArea: item.isCommonArea || false,
        order: item.order
      }));
      setDeficiencyTypes(deficiencyTypesData);
    } catch (err: any) {
      console.error('Error loading deficiency types:', err);
    }
  };

  const handleAddDeficiencyType = () => {
    setEditingDeficiencyType(null);
    setShowDeficiencyTypeModal(true);
  };

  const handleEditDeficiencyType = (deficiencyType: ListItem) => {
    setEditingDeficiencyType(deficiencyType);
    setShowDeficiencyTypeModal(true);
  };

  const handleDeleteDeficiencyType = (deficiencyTypeId: string) => {
    setDeficiencyTypeToDelete(deficiencyTypeId);
    setShowDeleteDeficiencyTypeConfirm(true);
  };

  const confirmDeleteDeficiencyType = async () => {
    if (!deficiencyTypeToDelete || !selectedProjectId) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/deficiency-types/${deficiencyTypeToDelete}`);
      setDeficiencyTypes(prev => prev.filter(dt => dt.id !== deficiencyTypeToDelete));
      toast.success('Deficiency type deleted successfully');
      setShowDeleteDeficiencyTypeConfirm(false);
      setDeficiencyTypeToDelete(null);
    } catch (err: any) {
      console.error('Error deleting deficiency type:', err);
      toast.error('Failed to delete deficiency type');
    }
  };

  const handleDragEndDeficiencyTypes = async (draggedIdx: number, dropIdx: number) => {
    if (!selectedProjectId || draggedIdx === dropIdx) return;

    const reordered = [...deficiencyTypes];
    const [movedItem] = reordered.splice(draggedIdx, 1);
    reordered.splice(dropIdx, 0, movedItem);

    const updated = reordered.map((dt, idx) => ({
      ...dt,
      order: idx
    }));

    setDeficiencyTypes(updated);

    try {
      const orderData = updated.map(dt => ({
        _id: dt.id,
        name: dt.name,
        isSuiteUnit: dt.isSuiteUnit,
        isCommonArea: dt.isCommonArea,
        order: dt.order
      }));
      await api.put(`/service-settings/${selectedProjectId}/deficiency-types/reorder`, { items: orderData });
      toast.success('Order updated successfully');
    } catch (err: any) {
      console.error('Error updating deficiency types order:', err);
      toast.error('Failed to update order');
      setDeficiencyTypes(deficiencyTypes);
    }
  };

  const handleSaveDeficiencyType = async (deficiencyTypeData: { name: string; isSuiteUnit: boolean; isCommonArea: boolean }) => {
    if (!selectedProjectId) return;

    try {
      if (editingDeficiencyType) {
        const response = await api.put(
          `/service-settings/${selectedProjectId}/deficiency-types/${editingDeficiencyType.id}`,
          deficiencyTypeData
        );
        const updatedDeficiencyType: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          isSuiteUnit: response.data.item.isSuiteUnit,
          isCommonArea: response.data.item.isCommonArea,
          order: response.data.item.order
        };
        setDeficiencyTypes(prev => prev.map(dt => dt.id === editingDeficiencyType.id ? updatedDeficiencyType : dt));
        toast.success('Deficiency type updated successfully');
      } else {
        const response = await api.post(`/service-settings/${selectedProjectId}/deficiency-types`, {
          ...deficiencyTypeData,
          order: deficiencyTypes.length
        });
        const newDeficiencyType: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          isSuiteUnit: response.data.item.isSuiteUnit,
          isCommonArea: response.data.item.isCommonArea,
          order: response.data.item.order
        };
        setDeficiencyTypes(prev => [...prev, newDeficiencyType]);
        toast.success('Deficiency type added successfully');
      }

      setShowDeficiencyTypeModal(false);
      setEditingDeficiencyType(null);
    } catch (err: any) {
      console.error('Error saving deficiency type:', err);
      toast.error('Failed to save deficiency type');
    }
  };

  // Priorities handlers
  const loadPriorities = async (projectId: string) => {
    try {
      const response = await api.get(`/service-settings/${projectId}/priorities`);
      const prioritiesData = response.data.items.map((item: any) => ({
        id: item._id,
        name: item.name,
        order: item.order
      }));
      setPriorities(prioritiesData);
    } catch (err: any) {
      console.error('Error loading priorities:', err);
    }
  };

  const handleAddPriority = () => {
    setEditingPriority(null);
    setShowPriorityModal(true);
  };

  const handleEditPriority = (priority: ListItem) => {
    setEditingPriority(priority);
    setShowPriorityModal(true);
  };

  const handleDeletePriority = (priorityId: string) => {
    setPriorityToDelete(priorityId);
    setShowDeletePriorityConfirm(true);
  };

  const confirmDeletePriority = async () => {
    if (!priorityToDelete || !selectedProjectId) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/priorities/${priorityToDelete}`);
      setPriorities(prev => prev.filter(p => p.id !== priorityToDelete));
      toast.success('Priority deleted successfully');
      setShowDeletePriorityConfirm(false);
      setPriorityToDelete(null);
    } catch (err: any) {
      console.error('Error deleting priority:', err);
      toast.error('Failed to delete priority');
    }
  };

  const handleDragEndPriorities = async (draggedIdx: number, dropIdx: number) => {
    if (!selectedProjectId || draggedIdx === dropIdx) return;

    const reordered = [...priorities];
    const [movedItem] = reordered.splice(draggedIdx, 1);
    reordered.splice(dropIdx, 0, movedItem);

    const updated = reordered.map((p, idx) => ({
      ...p,
      order: idx
    }));

    setPriorities(updated);

    try {
      const orderData = updated.map(p => ({ id: p.id, order: p.order }));
      await api.put(`/service-settings/${selectedProjectId}/priorities/reorder`, { items: orderData });
      toast.success('Order updated successfully');
    } catch (err: any) {
      console.error('Error updating priorities order:', err);
      toast.error('Failed to update order');
      setPriorities(priorities);
    }
  };

  const handleSavePriority = async (priorityData: { name: string }) => {
    if (!selectedProjectId) return;

    try {
      if (editingPriority) {
        const response = await api.put(
          `/service-settings/${selectedProjectId}/priorities/${editingPriority.id}`,
          { name: priorityData.name }
        );
        const updatedPriority: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          order: response.data.item.order
        };
        setPriorities(prev => prev.map(p => p.id === editingPriority.id ? updatedPriority : p));
        toast.success('Priority updated successfully');
      } else {
        const response = await api.post(`/service-settings/${selectedProjectId}/priorities`, {
          name: priorityData.name,
          order: priorities.length
        });
        const newPriority: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          order: response.data.item.order
        };
        setPriorities(prev => [...prev, newPriority]);
        toast.success('Priority added successfully');
      }

      setShowPriorityModal(false);
      setEditingPriority(null);
    } catch (err: any) {
      console.error('Error saving priority:', err);
      toast.error('Failed to save priority');
    }
  };

  // Trades handlers
  const loadTrades = async (projectId: string) => {
    try {
      const response = await api.get(`/service-settings/${projectId}/trades`);
      const tradesData = response.data.items.map((item: any) => ({
        id: item._id,
        name: item.name,
        order: item.order
      }));
      setTrades(tradesData);
    } catch (err: any) {
      console.error('Error loading trades:', err);
    }
  };

  const handleAddTrade = () => {
    setEditingTrade(null);
    setShowTradeModal(true);
  };

  const handleEditTrade = (trade: ListItem) => {
    setEditingTrade(trade);
    setShowTradeModal(true);
  };

  const handleDeleteTrade = (tradeId: string) => {
    setTradeToDelete(tradeId);
    setShowDeleteTradeConfirm(true);
  };

  const confirmDeleteTrade = async () => {
    if (!tradeToDelete || !selectedProjectId) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/trades/${tradeToDelete}`);
      setTrades(prev => prev.filter(t => t.id !== tradeToDelete));
      toast.success('Trade deleted successfully');
      setShowDeleteTradeConfirm(false);
      setTradeToDelete(null);
    } catch (err: any) {
      console.error('Error deleting trade:', err);
      toast.error('Failed to delete trade');
    }
  };

  const handleDragEndTrades = async (draggedIdx: number, dropIdx: number) => {
    if (!selectedProjectId || draggedIdx === dropIdx) return;

    const reordered = [...trades];
    const [movedItem] = reordered.splice(draggedIdx, 1);
    reordered.splice(dropIdx, 0, movedItem);

    const updated = reordered.map((t, idx) => ({
      ...t,
      order: idx
    }));

    setTrades(updated);

    try {
      const orderData = updated.map(t => ({ id: t.id, order: t.order }));
      await api.put(`/service-settings/${selectedProjectId}/trades/reorder`, { items: orderData });
      toast.success('Order updated successfully');
    } catch (err: any) {
      console.error('Error updating trades order:', err);
      toast.error('Failed to update order');
      setTrades(trades);
    }
  };

  const handleSaveTrade = async (tradeData: { name: string }) => {
    if (!selectedProjectId) return;

    try {
      if (editingTrade) {
        const response = await api.put(
          `/service-settings/${selectedProjectId}/trades/${editingTrade.id}`,
          { name: tradeData.name }
        );
        const updatedTrade: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          order: response.data.item.order
        };
        setTrades(prev => prev.map(t => t.id === editingTrade.id ? updatedTrade : t));
        toast.success('Trade updated successfully');
      } else {
        const response = await api.post(`/service-settings/${selectedProjectId}/trades`, {
          name: tradeData.name,
          order: trades.length
        });
        const newTrade: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          order: response.data.item.order
        };
        setTrades(prev => [...prev, newTrade]);
        toast.success('Trade added successfully');
      }

      setShowTradeModal(false);
      setEditingTrade(null);
    } catch (err: any) {
      console.error('Error saving trade:', err);
      toast.error('Failed to save trade');
    }
  };

  // Inspection Types handlers
  const loadInspectionTypes = async (projectId: string) => {
    try {
      const response = await api.get(`/service-settings/${projectId}/inspection-types`);
      const inspectionTypesData = response.data.items.map((item: any) => ({
        id: item._id,
        name: item.name,
        construction: item.construction || false,
        inspector: item.inspector || false,
        manager: item.manager || false,
        trade: item.trade || false,
        showOnPurchaserPortal: item.showOnPurchaserPortal || false,
        order: item.order
      }));
      setInspectionTypes(inspectionTypesData);
    } catch (err: any) {
      console.error('Error loading inspection types:', err);
    }
  };

  const handleAddInspectionType = () => {
    setEditingInspectionType(null);
    setShowInspectionTypeModal(true);
  };

  const handleEditInspectionType = (inspectionType: ListItem) => {
    setEditingInspectionType(inspectionType);
    setShowInspectionTypeModal(true);
  };

  const handleDeleteInspectionType = (id: string) => {
    setInspectionTypeToDelete(id);
    setShowDeleteInspectionTypeConfirm(true);
  };

  const confirmDeleteInspectionType = async () => {
    if (!inspectionTypeToDelete || !selectedProjectId) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/inspection-types/${inspectionTypeToDelete}`);
      setInspectionTypes(prev => prev.filter(it => it.id !== inspectionTypeToDelete));
      toast.success('Inspection type deleted successfully');
    } catch (err: any) {
      console.error('Error deleting inspection type:', err);
      toast.error('Failed to delete inspection type');
    } finally {
      setShowDeleteInspectionTypeConfirm(false);
      setInspectionTypeToDelete(null);
    }
  };

  const handleSaveInspectionType = async (inspectionTypeData: any) => {
    if (!selectedProjectId) return;

    try {
      if (editingInspectionType) {
        // Update existing inspection type
        await api.put(
          `/service-settings/${selectedProjectId}/inspection-types/${editingInspectionType.id}`,
          inspectionTypeData
        );
        setInspectionTypes(prev => prev.map(it =>
          it.id === editingInspectionType.id ? { ...it, ...inspectionTypeData } : it
        ));
        toast.success('Inspection type updated successfully');
      } else {
        // Add new inspection type
        const response = await api.post(`/service-settings/${selectedProjectId}/inspection-types`, {
          ...inspectionTypeData,
          order: inspectionTypes.length
        });
        const newInspectionType: ListItem = {
          id: response.data.item._id,
          name: response.data.item.name,
          construction: response.data.item.construction || false,
          inspector: response.data.item.inspector || false,
          manager: response.data.item.manager || false,
          trade: response.data.item.trade || false,
          showOnPurchaserPortal: response.data.item.showOnPurchaserPortal || false,
          order: response.data.item.order
        };
        setInspectionTypes(prev => [...prev, newInspectionType]);
        toast.success('Inspection type added successfully');
      }

      setShowInspectionTypeModal(false);
      setEditingInspectionType(null);
    } catch (err: any) {
      console.error('Error saving inspection type:', err);
      toast.error('Failed to save inspection type');
    }
  };

  const handleDragEndInspectionTypes = async (draggedIdx: number, dropIdx: number) => {
    if (!selectedProjectId || draggedIdx === dropIdx) return;

    const reordered = [...inspectionTypes];
    const [movedItem] = reordered.splice(draggedIdx, 1);
    reordered.splice(dropIdx, 0, movedItem);

    const updated = reordered.map((it, idx) => ({
      ...it,
      order: idx
    }));

    setInspectionTypes(updated);

    try {
      const orderData = updated.map(it => ({
        _id: it.id,
        name: it.name,
        construction: it.construction || false,
        inspector: it.inspector || false,
        manager: it.manager || false,
        trade: it.trade || false,
        order: it.order
      }));
      await api.put(`/service-settings/${selectedProjectId}/inspection-types/reorder`, { items: orderData });
      toast.success('Order updated successfully');
    } catch (err: any) {
      console.error('Error updating inspection types order:', err);
      toast.error('Failed to update order');
      setInspectionTypes(inspectionTypes);
    }
  };

  // Users handlers
  const loadUsers = async (projectId: string) => {
    try {
      const response = await api.get(`/service-settings/${projectId}/users`);
      setUsers(response.data.users);
    } catch (err: any) {
      console.error('Error loading users:', err);
    }
  };

  const loadContacts = async (searchQuery = '', page = 1) => {
    if (!workspaceId) return;

    try {
      const params: any = {
        page,
        limit: 20,
        sortBy: 'displayName',
        sortOrder: 'asc'
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get(`/workspaces/${workspaceId}/contacts`, { params });
      setContacts(response.data.contacts || []);
      if (response.data.pagination) {
        setContactsPagination(response.data.pagination);
      }
    } catch (err: any) {
      console.error('Error loading contacts:', err);
    }
  };

  const handleAddUsers = () => {
    loadContacts();
    setShowAddUsersModal(true);
  };

  const handleSaveUsers = async (selectedContacts: Array<{ contactId: string; name: string; email: string; construction: boolean; inspector: boolean; manager: boolean; trade: boolean }>) => {
    if (!selectedProjectId) return;

    try {
      for (const contact of selectedContacts) {
        await api.post(`/service-settings/${selectedProjectId}/users`, contact);
      }

      loadUsers(selectedProjectId);
      setShowAddUsersModal(false);
      toast.success('Users added successfully');
    } catch (err: any) {
      console.error('Error adding users:', err);
      toast.error('Failed to add users');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditUserModal(true);
  };

  const handleSaveEditUser = async (userData: { construction: boolean; inspector: boolean; manager: boolean; trade: boolean; tradeIds: string[] }) => {
    if (!selectedProjectId || !editingUser) return;

    try {
      await api.put(`/service-settings/${selectedProjectId}/users/${editingUser.contactId}`, userData);

      setUsers(prev => prev.map(u =>
        u.contactId === editingUser.contactId
          ? { ...u, ...userData }
          : u
      ));

      setShowEditUserModal(false);
      setEditingUser(null);
      toast.success('User updated successfully');
    } catch (err: any) {
      console.error('Error updating user:', err);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = (contactId: string) => {
    setUserToDelete(contactId);
    setShowDeleteUserConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete || !selectedProjectId) return;

    try {
      await api.delete(`/service-settings/${selectedProjectId}/users/${userToDelete}`);
      setUsers(prev => prev.filter(u => u.contactId !== userToDelete));
      setShowDeleteUserConfirm(false);
      setUserToDelete(null);
      toast.success('User deleted successfully');
    } catch (err: any) {
      console.error('Error deleting user:', err);
      toast.error('Failed to delete user');
    }
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  const handleSave = async () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }

    setSaving(true);
    try {
      // Only send the settings data relevant to the current tab
      let settingsToUpdate: any = {};

      if (selectedTab === 'roles') {
        settingsToUpdate = { roles: settings.roles };
      } else if (selectedTab === 'emailNotifications') {
        settingsToUpdate = { emailNotifications: settings.emailNotifications };
      }

      await api.put(`/service-settings/${selectedProjectId}`, settingsToUpdate);
      setSaving(false);
      toast.success('Settings saved successfully');
    } catch (err: any) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings');
      setSaving(false);
    }
  };

  const handleToggle = (section: 'roles', key: string) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: !prev[section][key as keyof typeof prev.roles]
      }
    }));
  };

  const renderToggleSwitch = (checked: boolean) => {
    return (
      <div style={{
        position: 'relative',
        width: '44px',
        height: '24px',
        backgroundColor: checked ? '#48182F' : '#D4CBC4',
        borderRadius: '12px',
        transition: 'background-color 0.2s',
        cursor: 'pointer'
      }}>
        <div style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          width: '20px',
          height: '20px',
          backgroundColor: '#FFFFFF',
          borderRadius: '50%',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }} />
      </div>
    );
  };

  const tabs = [
    { key: 'roles', label: 'Roles' },
    { key: 'locations', label: 'Locations' },
    { key: 'tags', label: 'Tags' },
    { key: 'deficiencyTypes', label: 'Type Of Deficiency' },
    { key: 'priorities', label: 'Priority' },
    { key: 'trades', label: 'Trades' },
    { key: 'inspectionTypes', label: 'Inspection Type' },
    { key: 'users', label: 'Users' },
    { key: 'emailNotifications', label: 'Email Notifications' }
  ];

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Page Header with Project Dropdown */}
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Service Settings</h1>
              <p className="builders-section-subtitle">
                Configure service module preferences
              </p>
            </div>
            <div style={{ marginTop: '4px' }}>
              <select
                className="form-input"
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={loading || projects.length === 0}
                style={{ minWidth: '280px' }}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '20px' }}>
              {/* Sidebar Tabs */}
              <div style={{
                width: '220px',
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '8px',
                height: 'fit-content',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setSelectedTab(tab.key)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: selectedTab === tab.key ? '#48182F' : 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: selectedTab === tab.key ? '600' : '400',
                      color: selectedTab === tab.key ? '#FFFFFF' : '#6B7280',
                      textAlign: 'left',
                      marginBottom: '4px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Settings Content */}
              <div style={{ flex: 1 }}>
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  {selectedTab === 'roles' && settings?.roles && (
                    <div>
                      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                        Service Roles
                      </h2>
                      <p style={{ color: '#6B7280', marginBottom: '24px', fontSize: '14px' }}>
                        Enable or disable roles for service module assignments
                      </p>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Construction Role */}
                        <div
                          onClick={() => handleToggle('roles', 'construction')}
                          style={{
                            padding: '16px',
                            backgroundColor: '#F5F3F0',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            border: '1px solid #D4CBC4',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#E8DED8';
                            e.currentTarget.style.borderColor = '#A89088';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5F3F0';
                            e.currentTarget.style.borderColor = '#D4CBC4';
                          }}
                        >
                          <div>
                            <p style={{ fontWeight: '500', marginBottom: '4px', fontSize: '14px' }}>
                              CONSTRUCTION
                            </p>
                            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                              Construction team members and supervisors
                            </p>
                          </div>
                          {renderToggleSwitch(settings.roles.construction)}
                        </div>

                        {/* Inspector Role */}
                        <div
                          onClick={() => handleToggle('roles', 'inspector')}
                          style={{
                            padding: '16px',
                            backgroundColor: '#F5F3F0',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            border: '1px solid #D4CBC4',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#E8DED8';
                            e.currentTarget.style.borderColor = '#A89088';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5F3F0';
                            e.currentTarget.style.borderColor = '#D4CBC4';
                          }}
                        >
                          <div>
                            <p style={{ fontWeight: '500', marginBottom: '4px', fontSize: '14px' }}>
                              INSPECTOR
                            </p>
                            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                              Quality control and inspection personnel
                            </p>
                          </div>
                          {renderToggleSwitch(settings.roles.inspector)}
                        </div>

                        {/* Manager Role */}
                        <div
                          onClick={() => handleToggle('roles', 'manager')}
                          style={{
                            padding: '16px',
                            backgroundColor: '#F5F3F0',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            border: '1px solid #D4CBC4',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#E8DED8';
                            e.currentTarget.style.borderColor = '#A89088';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5F3F0';
                            e.currentTarget.style.borderColor = '#D4CBC4';
                          }}
                        >
                          <div>
                            <p style={{ fontWeight: '500', marginBottom: '4px', fontSize: '14px' }}>
                              MANAGER
                            </p>
                            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                              Project managers and coordinators
                            </p>
                          </div>
                          {renderToggleSwitch(settings.roles.manager)}
                        </div>

                        {/* Trade Role */}
                        <div
                          onClick={() => handleToggle('roles', 'trade')}
                          style={{
                            padding: '16px',
                            backgroundColor: '#F5F3F0',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            border: '1px solid #D4CBC4',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#E8DED8';
                            e.currentTarget.style.borderColor = '#A89088';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5F3F0';
                            e.currentTarget.style.borderColor = '#D4CBC4';
                          }}
                        >
                          <div>
                            <p style={{ fontWeight: '500', marginBottom: '4px', fontSize: '14px' }}>
                              TRADE
                            </p>
                            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                              Trade contractors and specialists
                            </p>
                          </div>
                          {renderToggleSwitch(settings.roles.trade)}
                        </div>
                      </div>

                      {/* Save Button */}
                      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedTab === 'locations' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Issue Locations
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage deficiency and inspection location options
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddLocation}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Locations Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th style={{ width: '40%' }}>Name</th>
                              <th style={{ width: '15%', textAlign: 'center' }}>Suite/Unit</th>
                              <th style={{ width: '15%', textAlign: 'center' }}>Common Area</th>
                              <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {locations.length === 0 ? (
                              <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No locations added yet. Click "Add" to create your first location.
                                </td>
                              </tr>
                            ) : (
                              locations
                                .slice((locationsPage - 1) * locationsPerPage, locationsPage * locationsPerPage)
                                .map((location, index) => {
                                  const actualIndex = (locationsPage - 1) * locationsPerPage + index;
                                  return (
                                    <tr
                                      key={location.id}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, actualIndex)}
                                      onDragOver={(e) => handleDragOver(e, actualIndex)}
                                      onDragLeave={handleDragLeave}
                                      onDrop={(e) => handleDrop(e, actualIndex)}
                                      onDragEnd={handleDragEnd}
                                      style={{
                                        opacity: draggedIndex === actualIndex ? 0.4 : 1,
                                        backgroundColor: dragOverIndex === actualIndex ? '#F3F4F6' : (index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'),
                                        cursor: 'move'
                                      }}
                                    >
                                      <td style={{ cursor: 'grab', padding: '8px' }}>
                                        <GripVertical size={20} color="#9CA3AF" />
                                      </td>
                                      <td>{location.name}</td>
                                      <td style={{ textAlign: 'center' }}>
                                        {location.isSuiteUnit && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                      </td>
                                      <td style={{ textAlign: 'center' }}>
                                        {location.isCommonArea && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditLocation(location);
                                            }}
                                            title="Edit"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#48182F'
                                            }}
                                          >
                                            <Pencil size={16} />
                                          </button>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteLocation(location.id);
                                            }}
                                            title="Delete"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#DC2626'
                                            }}
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Locations Pagination */}
                      {locations.length > locationsPerPage && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 8px' }}>
                          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                            Showing {((locationsPage - 1) * locationsPerPage) + 1} to {Math.min(locationsPage * locationsPerPage, locations.length)} of {locations.length} locations
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => setLocationsPage(1)}
                              disabled={locationsPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: locationsPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: locationsPage === 1 ? 'not-allowed' : 'pointer',
                                color: locationsPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              First
                            </button>
                            <button
                              onClick={() => setLocationsPage(p => Math.max(1, p - 1))}
                              disabled={locationsPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: locationsPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: locationsPage === 1 ? 'not-allowed' : 'pointer',
                                color: locationsPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Previous
                            </button>
                            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#374151' }}>
                              Page {locationsPage} of {Math.ceil(locations.length / locationsPerPage)}
                            </span>
                            <button
                              onClick={() => setLocationsPage(p => Math.min(Math.ceil(locations.length / locationsPerPage), p + 1))}
                              disabled={locationsPage >= Math.ceil(locations.length / locationsPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: locationsPage >= Math.ceil(locations.length / locationsPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: locationsPage >= Math.ceil(locations.length / locationsPerPage) ? 'not-allowed' : 'pointer',
                                color: locationsPage >= Math.ceil(locations.length / locationsPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setLocationsPage(Math.ceil(locations.length / locationsPerPage))}
                              disabled={locationsPage >= Math.ceil(locations.length / locationsPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: locationsPage >= Math.ceil(locations.length / locationsPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: locationsPage >= Math.ceil(locations.length / locationsPerPage) ? 'not-allowed' : 'pointer',
                                color: locationsPage >= Math.ceil(locations.length / locationsPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTab === 'tags' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Tags
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage service tags for categorization
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddTag}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Tags Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th style={{ width: '40%' }}>Name</th>
                              <th style={{ width: '15%', textAlign: 'center' }}>Suite/Unit</th>
                              <th style={{ width: '15%', textAlign: 'center' }}>Common Area</th>
                              <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tags.length === 0 ? (
                              <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No tags added yet. Click "Add" to create your first tag.
                                </td>
                              </tr>
                            ) : (
                              tags
                                .slice((tagsPage - 1) * tagsPerPage, tagsPage * tagsPerPage)
                                .map((tag, index) => {
                                  const actualIndex = (tagsPage - 1) * tagsPerPage + index;
                                  return (
                                    <tr
                                      key={tag.id}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, actualIndex)}
                                      onDragOver={(e) => handleDragOver(e, actualIndex)}
                                      onDragLeave={handleDragLeave}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (draggedIndex !== null && draggedIndex !== actualIndex) {
                                          handleDragEndTags(draggedIndex, actualIndex);
                                        }
                                        setDraggedIndex(null);
                                        setDragOverIndex(null);
                                      }}
                                      onDragEnd={handleDragEnd}
                                      style={{
                                        opacity: draggedIndex === actualIndex ? 0.4 : 1,
                                        backgroundColor: dragOverIndex === actualIndex ? '#F3F4F6' : (index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'),
                                        cursor: 'move'
                                      }}
                                    >
                                      <td style={{ cursor: 'grab', padding: '8px' }}>
                                        <GripVertical size={20} color="#9CA3AF" />
                                      </td>
                                      <td>{tag.name}</td>
                                      <td style={{ textAlign: 'center' }}>
                                        {tag.isSuiteUnit && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                      </td>
                                      <td style={{ textAlign: 'center' }}>
                                        {tag.isCommonArea && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditTag(tag);
                                            }}
                                            title="Edit"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#48182F'
                                            }}
                                          >
                                            <Pencil size={16} />
                                          </button>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteTag(tag.id);
                                            }}
                                            title="Delete"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#DC2626'
                                            }}
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Tags Pagination */}
                      {tags.length > tagsPerPage && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 8px' }}>
                          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                            Showing {((tagsPage - 1) * tagsPerPage) + 1} to {Math.min(tagsPage * tagsPerPage, tags.length)} of {tags.length} tags
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => setTagsPage(1)}
                              disabled={tagsPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tagsPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: tagsPage === 1 ? 'not-allowed' : 'pointer',
                                color: tagsPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              First
                            </button>
                            <button
                              onClick={() => setTagsPage(p => Math.max(1, p - 1))}
                              disabled={tagsPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tagsPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: tagsPage === 1 ? 'not-allowed' : 'pointer',
                                color: tagsPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Previous
                            </button>
                            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#374151' }}>
                              Page {tagsPage} of {Math.ceil(tags.length / tagsPerPage)}
                            </span>
                            <button
                              onClick={() => setTagsPage(p => Math.min(Math.ceil(tags.length / tagsPerPage), p + 1))}
                              disabled={tagsPage >= Math.ceil(tags.length / tagsPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tagsPage >= Math.ceil(tags.length / tagsPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: tagsPage >= Math.ceil(tags.length / tagsPerPage) ? 'not-allowed' : 'pointer',
                                color: tagsPage >= Math.ceil(tags.length / tagsPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setTagsPage(Math.ceil(tags.length / tagsPerPage))}
                              disabled={tagsPage >= Math.ceil(tags.length / tagsPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tagsPage >= Math.ceil(tags.length / tagsPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: tagsPage >= Math.ceil(tags.length / tagsPerPage) ? 'not-allowed' : 'pointer',
                                color: tagsPage >= Math.ceil(tags.length / tagsPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTab === 'deficiencyTypes' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Type Of Deficiency
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage deficiency type options
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddDeficiencyType}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Deficiency Types Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th style={{ width: '40%' }}>Name</th>
                              <th style={{ width: '15%', textAlign: 'center' }}>Suite/Unit</th>
                              <th style={{ width: '15%', textAlign: 'center' }}>Common Area</th>
                              <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deficiencyTypes.length === 0 ? (
                              <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No deficiency types added yet. Click "Add" to create your first deficiency type.
                                </td>
                              </tr>
                            ) : (
                              deficiencyTypes
                                .slice((deficiencyTypesPage - 1) * deficiencyTypesPerPage, deficiencyTypesPage * deficiencyTypesPerPage)
                                .map((deficiencyType, index) => {
                                  const actualIndex = (deficiencyTypesPage - 1) * deficiencyTypesPerPage + index;
                                  return (
                                    <tr
                                      key={deficiencyType.id}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, actualIndex)}
                                      onDragOver={(e) => handleDragOver(e, actualIndex)}
                                      onDragLeave={handleDragLeave}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (draggedIndex !== null && draggedIndex !== actualIndex) {
                                          handleDragEndDeficiencyTypes(draggedIndex, actualIndex);
                                        }
                                        setDraggedIndex(null);
                                        setDragOverIndex(null);
                                      }}
                                      onDragEnd={handleDragEnd}
                                      style={{
                                        opacity: draggedIndex === actualIndex ? 0.4 : 1,
                                        backgroundColor: dragOverIndex === actualIndex ? '#F3F4F6' : (index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'),
                                        cursor: 'move'
                                      }}
                                    >
                                      <td style={{ cursor: 'grab', padding: '8px' }}>
                                        <GripVertical size={20} color="#9CA3AF" />
                                      </td>
                                      <td>{deficiencyType.name}</td>
                                      <td style={{ textAlign: 'center' }}>
                                        {deficiencyType.isSuiteUnit && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                      </td>
                                      <td style={{ textAlign: 'center' }}>
                                        {deficiencyType.isCommonArea && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditDeficiencyType(deficiencyType);
                                            }}
                                            title="Edit"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#48182F'
                                            }}
                                          >
                                            <Pencil size={16} />
                                          </button>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteDeficiencyType(deficiencyType.id);
                                            }}
                                            title="Delete"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#DC2626'
                                            }}
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Deficiency Types Pagination */}
                      {deficiencyTypes.length > deficiencyTypesPerPage && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 8px' }}>
                          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                            Showing {((deficiencyTypesPage - 1) * deficiencyTypesPerPage) + 1} to {Math.min(deficiencyTypesPage * deficiencyTypesPerPage, deficiencyTypes.length)} of {deficiencyTypes.length} deficiency types
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => setDeficiencyTypesPage(1)}
                              disabled={deficiencyTypesPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: deficiencyTypesPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: deficiencyTypesPage === 1 ? 'not-allowed' : 'pointer',
                                color: deficiencyTypesPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              First
                            </button>
                            <button
                              onClick={() => setDeficiencyTypesPage(p => Math.max(1, p - 1))}
                              disabled={deficiencyTypesPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: deficiencyTypesPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: deficiencyTypesPage === 1 ? 'not-allowed' : 'pointer',
                                color: deficiencyTypesPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Previous
                            </button>
                            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#374151' }}>
                              Page {deficiencyTypesPage} of {Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage)}
                            </span>
                            <button
                              onClick={() => setDeficiencyTypesPage(p => Math.min(Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage), p + 1))}
                              disabled={deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage) ? 'not-allowed' : 'pointer',
                                color: deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setDeficiencyTypesPage(Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage))}
                              disabled={deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage) ? 'not-allowed' : 'pointer',
                                color: deficiencyTypesPage >= Math.ceil(deficiencyTypes.length / deficiencyTypesPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTab === 'priorities' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Priority
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage priority levels
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddPriority}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Priorities Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th style={{ width: '80%' }}>Name</th>
                              <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {priorities.length === 0 ? (
                              <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No priorities added yet. Click "Add" to create your first priority.
                                </td>
                              </tr>
                            ) : (
                              priorities.map((priority, index) => (
                                <tr
                                  key={priority.id}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, index)}
                                  onDragOver={(e) => handleDragOver(e, index)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (draggedIndex !== null && draggedIndex !== index) {
                                      handleDragEndPriorities(draggedIndex, index);
                                    }
                                    setDraggedIndex(null);
                                    setDragOverIndex(null);
                                  }}
                                  onDragEnd={handleDragEnd}
                                  style={{
                                    opacity: draggedIndex === index ? 0.4 : 1,
                                    backgroundColor: dragOverIndex === index ? '#F3F4F6' : (index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'),
                                    cursor: 'move'
                                  }}
                                >
                                  <td style={{ cursor: 'grab', padding: '8px' }}>
                                    <GripVertical size={20} color="#9CA3AF" />
                                  </td>
                                  <td>{priority.name}</td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                      <button
                                        className="btn-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditPriority(priority);
                                        }}
                                        title="Edit"
                                        style={{
                                          border: 'none',
                                          background: 'none',
                                          cursor: 'pointer',
                                          padding: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: '#48182F'
                                        }}
                                      >
                                        <Pencil size={16} />
                                      </button>
                                      <button
                                        className="btn-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeletePriority(priority.id);
                                        }}
                                        title="Delete"
                                        style={{
                                          border: 'none',
                                          background: 'none',
                                          cursor: 'pointer',
                                          padding: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: '#DC2626'
                                        }}
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {selectedTab === 'trades' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Trades
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage trade contractors and specialists
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddTrade}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Trades Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th style={{ width: '80%' }}>Name</th>
                              <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trades.length === 0 ? (
                              <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No trades added yet. Click "Add" to create your first trade.
                                </td>
                              </tr>
                            ) : (
                              trades
                                .slice((tradesPage - 1) * tradesPerPage, tradesPage * tradesPerPage)
                                .map((trade, index) => {
                                  const actualIndex = (tradesPage - 1) * tradesPerPage + index;
                                  return (
                                    <tr
                                      key={trade.id}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, actualIndex)}
                                      onDragOver={(e) => handleDragOver(e, actualIndex)}
                                      onDragLeave={handleDragLeave}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (draggedIndex !== null && draggedIndex !== actualIndex) {
                                          handleDragEndTrades(draggedIndex, actualIndex);
                                        }
                                        setDraggedIndex(null);
                                        setDragOverIndex(null);
                                      }}
                                      onDragEnd={handleDragEnd}
                                      style={{
                                        opacity: draggedIndex === actualIndex ? 0.4 : 1,
                                        backgroundColor: dragOverIndex === actualIndex ? '#F3F4F6' : (index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'),
                                        cursor: 'move'
                                      }}
                                    >
                                      <td style={{ cursor: 'grab', padding: '8px' }}>
                                        <GripVertical size={20} color="#9CA3AF" />
                                      </td>
                                      <td>{trade.name}</td>
                                      <td>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditTrade(trade);
                                            }}
                                            title="Edit"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#48182F'
                                            }}
                                          >
                                            <Pencil size={16} />
                                          </button>
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteTrade(trade.id);
                                            }}
                                            title="Delete"
                                            style={{
                                              border: 'none',
                                              background: 'none',
                                              cursor: 'pointer',
                                              padding: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#DC2626'
                                            }}
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Trades Pagination */}
                      {trades.length > tradesPerPage && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 8px' }}>
                          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                            Showing {((tradesPage - 1) * tradesPerPage) + 1} to {Math.min(tradesPage * tradesPerPage, trades.length)} of {trades.length} trades
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => setTradesPage(1)}
                              disabled={tradesPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tradesPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: tradesPage === 1 ? 'not-allowed' : 'pointer',
                                color: tradesPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              First
                            </button>
                            <button
                              onClick={() => setTradesPage(p => Math.max(1, p - 1))}
                              disabled={tradesPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tradesPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: tradesPage === 1 ? 'not-allowed' : 'pointer',
                                color: tradesPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Previous
                            </button>
                            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#374151' }}>
                              Page {tradesPage} of {Math.ceil(trades.length / tradesPerPage)}
                            </span>
                            <button
                              onClick={() => setTradesPage(p => Math.min(Math.ceil(trades.length / tradesPerPage), p + 1))}
                              disabled={tradesPage >= Math.ceil(trades.length / tradesPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tradesPage >= Math.ceil(trades.length / tradesPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: tradesPage >= Math.ceil(trades.length / tradesPerPage) ? 'not-allowed' : 'pointer',
                                color: tradesPage >= Math.ceil(trades.length / tradesPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setTradesPage(Math.ceil(trades.length / tradesPerPage))}
                              disabled={tradesPage >= Math.ceil(trades.length / tradesPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: tradesPage >= Math.ceil(trades.length / tradesPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: tradesPage >= Math.ceil(trades.length / tradesPerPage) ? 'not-allowed' : 'pointer',
                                color: tradesPage >= Math.ceil(trades.length / tradesPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTab === 'inspectionTypes' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Inspection Types
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage inspection type categories and role assignments
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddInspectionType}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Inspection Types Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={{ width: '3%' }}></th>
                              <th style={{ width: '25%' }}>Name</th>
                              <th style={{ width: '12%', textAlign: 'center' }}>Construction</th>
                              <th style={{ width: '12%', textAlign: 'center' }}>Inspector</th>
                              <th style={{ width: '12%', textAlign: 'center' }}>Manager</th>
                              <th style={{ width: '12%', textAlign: 'center' }}>Trade</th>
                              <th style={{ width: '12%', textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inspectionTypes.length === 0 ? (
                              <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No inspection types added yet. Click "Add" to create your first inspection type.
                                </td>
                              </tr>
                            ) : (
                              inspectionTypes.map((inspectionType, index) => (
                                <tr
                                  key={inspectionType.id}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, index)}
                                  onDragOver={(e) => handleDragOver(e, index)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (draggedIndex !== null && draggedIndex !== index) {
                                      handleDragEndInspectionTypes(draggedIndex, index);
                                    }
                                    setDraggedIndex(null);
                                    setDragOverIndex(null);
                                  }}
                                  onDragEnd={handleDragEnd}
                                  style={{
                                    opacity: draggedIndex === index ? 0.4 : 1,
                                    backgroundColor: dragOverIndex === index ? '#F3F4F6' : (index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'),
                                    cursor: 'move'
                                  }}
                                >
                                  <td style={{ cursor: 'grab', padding: '8px' }}>
                                    <GripVertical size={20} color="#9CA3AF" />
                                  </td>
                                  <td>{inspectionType.name}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    {inspectionType.construction && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {inspectionType.inspector && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {inspectionType.manager && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {inspectionType.trade && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                      <button
                                        className="btn-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditInspectionType(inspectionType);
                                        }}
                                        title="Edit"
                                        style={{
                                          border: 'none',
                                          background: 'none',
                                          cursor: 'pointer',
                                          padding: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: '#48182F'
                                        }}
                                      >
                                        <Pencil size={16} />
                                      </button>
                                      <button
                                        className="btn-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteInspectionType(inspectionType.id);
                                        }}
                                        title="Delete"
                                        style={{
                                          border: 'none',
                                          background: 'none',
                                          cursor: 'pointer',
                                          padding: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: '#DC2626'
                                        }}
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {selectedTab === 'users' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                            Users
                          </h2>
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                            Manage users and assign roles for service modules
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleAddUsers}
                        >
                          <Plus size={20} />
                          Add
                        </button>
                      </div>

                      {/* Users Table */}
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table className="data-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th style={{ textAlign: 'center' }}>Construction</th>
                              <th style={{ textAlign: 'center' }}>Inspector</th>
                              <th style={{ textAlign: 'center' }}>Manager</th>
                              <th style={{ textAlign: 'center' }}>Trade</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.length === 0 ? (
                              <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                  No users added yet. Click "Add" to assign users to this project.
                                </td>
                              </tr>
                            ) : (
                              users
                                .slice((usersPage - 1) * usersPerPage, usersPage * usersPerPage)
                                .map((user, index) => (
                                  <tr
                                    key={user.contactId}
                                    style={{
                                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                                    }}
                                  >
                                    <td>{user.name}</td>
                                    <td>{user.email}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      {user.construction && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      {user.inspector && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      {user.manager && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      {user.trade && <span style={{ color: '#48182F', fontSize: '18px' }}>✓</span>}
                                    </td>
                                    <td>
                                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <button
                                          className="btn-icon"
                                          onClick={() => handleEditUser(user)}
                                          title="Edit"
                                          style={{
                                            border: 'none',
                                            background: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#48182F'
                                          }}
                                        >
                                          <Pencil size={16} />
                                        </button>
                                        <button
                                          className="btn-icon"
                                          onClick={() => handleDeleteUser(user.contactId)}
                                          title="Delete"
                                          style={{
                                            border: 'none',
                                            background: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#DC2626'
                                          }}
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Users Pagination */}
                      {users.length > usersPerPage && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '0 8px' }}>
                          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                            Showing {((usersPage - 1) * usersPerPage) + 1} to {Math.min(usersPage * usersPerPage, users.length)} of {users.length} users
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => setUsersPage(1)}
                              disabled={usersPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: usersPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: usersPage === 1 ? 'not-allowed' : 'pointer',
                                color: usersPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              First
                            </button>
                            <button
                              onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                              disabled={usersPage === 1}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: usersPage === 1 ? '#F3F4F6' : '#FFFFFF',
                                cursor: usersPage === 1 ? 'not-allowed' : 'pointer',
                                color: usersPage === 1 ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Previous
                            </button>
                            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#374151' }}>
                              Page {usersPage} of {Math.ceil(users.length / usersPerPage)}
                            </span>
                            <button
                              onClick={() => setUsersPage(p => Math.min(Math.ceil(users.length / usersPerPage), p + 1))}
                              disabled={usersPage >= Math.ceil(users.length / usersPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: usersPage >= Math.ceil(users.length / usersPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: usersPage >= Math.ceil(users.length / usersPerPage) ? 'not-allowed' : 'pointer',
                                color: usersPage >= Math.ceil(users.length / usersPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Next
                            </button>
                            <button
                              onClick={() => setUsersPage(Math.ceil(users.length / usersPerPage))}
                              disabled={usersPage >= Math.ceil(users.length / usersPerPage)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '4px',
                                background: usersPage >= Math.ceil(users.length / usersPerPage) ? '#F3F4F6' : '#FFFFFF',
                                cursor: usersPage >= Math.ceil(users.length / usersPerPage) ? 'not-allowed' : 'pointer',
                                color: usersPage >= Math.ceil(users.length / usersPerPage) ? '#9CA3AF' : '#374151'
                              }}
                            >
                              Last
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTab === 'emailNotifications' && settings?.emailNotifications && (
                    <div>
                      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
                        Email Notifications
                      </h2>

                      {/* Construction Users */}
                      <div style={{ marginBottom: '32px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                          Construction Users
                        </h3>

                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="construction-frequency"
                              checked={settings.emailNotifications.construction.frequency === 'none'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  construction: { ...prev.emailNotifications.construction, frequency: 'none' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>None</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="construction-frequency"
                              checked={settings.emailNotifications.construction.frequency === 'daily'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  construction: { ...prev.emailNotifications.construction, frequency: 'daily' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Daily</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="construction-frequency"
                              checked={settings.emailNotifications.construction.frequency === 'weekly'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  construction: { ...prev.emailNotifications.construction, frequency: 'weekly' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Weekly</span>
                          </label>

                          {settings.emailNotifications.construction.frequency !== 'none' && (
                            <select
                              value={settings.emailNotifications.construction.time}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  construction: { ...prev.emailNotifications.construction, time: e.target.value }
                                }
                              }))}
                              className="form-input"
                              style={{ width: '120px' }}
                            >
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, '0');
                                return <option key={hour} value={`${hour}:00`}>{`${hour}:00`}</option>;
                              })}
                            </select>
                          )}
                        </div>

                        {settings.emailNotifications.construction.frequency === 'weekly' && (
                          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                              <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={settings.emailNotifications.construction.days[day as keyof typeof settings.emailNotifications.construction.days]}
                                  onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    emailNotifications: {
                                      ...prev.emailNotifications,
                                      construction: {
                                        ...prev.emailNotifications.construction,
                                        days: {
                                          ...prev.emailNotifications.construction.days,
                                          [day]: e.target.checked
                                        }
                                      }
                                    }
                                  }))}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{day}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>CC Emails</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Please enter comma(,) separated email addresses for multiple CC emails."
                            value={settings.emailNotifications.construction.ccEmails}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                construction: { ...prev.emailNotifications.construction, ccEmails: e.target.value }
                              }
                            }))}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Notes</label>
                          <textarea
                            className="form-input"
                            rows={3}
                            placeholder="This note will be sent as body of email notification."
                            value={settings.emailNotifications.construction.notes}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                construction: { ...prev.emailNotifications.construction, notes: e.target.value }
                              }
                            }))}
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      {/* Inspector Users */}
                      <div style={{ marginBottom: '32px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                          Inspector Users
                        </h3>

                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="inspector-frequency"
                              checked={settings.emailNotifications.inspector.frequency === 'none'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  inspector: { ...prev.emailNotifications.inspector, frequency: 'none' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>None</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="inspector-frequency"
                              checked={settings.emailNotifications.inspector.frequency === 'daily'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  inspector: { ...prev.emailNotifications.inspector, frequency: 'daily' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Daily</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="inspector-frequency"
                              checked={settings.emailNotifications.inspector.frequency === 'weekly'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  inspector: { ...prev.emailNotifications.inspector, frequency: 'weekly' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Weekly</span>
                          </label>

                          {settings.emailNotifications.inspector.frequency !== 'none' && (
                            <select
                              value={settings.emailNotifications.inspector.time}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  inspector: { ...prev.emailNotifications.inspector, time: e.target.value }
                                }
                              }))}
                              className="form-input"
                              style={{ width: '120px' }}
                            >
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, '0');
                                return <option key={hour} value={`${hour}:00`}>{`${hour}:00`}</option>;
                              })}
                            </select>
                          )}
                        </div>

                        {settings.emailNotifications.inspector.frequency === 'weekly' && (
                          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                              <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={settings.emailNotifications.inspector.days[day as keyof typeof settings.emailNotifications.inspector.days]}
                                  onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    emailNotifications: {
                                      ...prev.emailNotifications,
                                      inspector: {
                                        ...prev.emailNotifications.inspector,
                                        days: {
                                          ...prev.emailNotifications.inspector.days,
                                          [day]: e.target.checked
                                        }
                                      }
                                    }
                                  }))}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{day}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>CC Emails</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Please enter comma(,) separated email addresses for multiple CC emails."
                            value={settings.emailNotifications.inspector.ccEmails}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                inspector: { ...prev.emailNotifications.inspector, ccEmails: e.target.value }
                              }
                            }))}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Notes</label>
                          <textarea
                            className="form-input"
                            rows={3}
                            placeholder="This note will be sent as body of email notification."
                            value={settings.emailNotifications.inspector.notes}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                inspector: { ...prev.emailNotifications.inspector, notes: e.target.value }
                              }
                            }))}
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      {/* Manager Users */}
                      <div style={{ marginBottom: '32px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                          Manager Users
                        </h3>

                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="manager-frequency"
                              checked={settings.emailNotifications.manager.frequency === 'none'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  manager: { ...prev.emailNotifications.manager, frequency: 'none' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>None</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="manager-frequency"
                              checked={settings.emailNotifications.manager.frequency === 'daily'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  manager: { ...prev.emailNotifications.manager, frequency: 'daily' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Daily</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="manager-frequency"
                              checked={settings.emailNotifications.manager.frequency === 'weekly'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  manager: { ...prev.emailNotifications.manager, frequency: 'weekly' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Weekly</span>
                          </label>

                          {settings.emailNotifications.manager.frequency !== 'none' && (
                            <select
                              value={settings.emailNotifications.manager.time}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  manager: { ...prev.emailNotifications.manager, time: e.target.value }
                                }
                              }))}
                              className="form-input"
                              style={{ width: '120px' }}
                            >
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, '0');
                                return <option key={hour} value={`${hour}:00`}>{`${hour}:00`}</option>;
                              })}
                            </select>
                          )}
                        </div>

                        {settings.emailNotifications.manager.frequency === 'weekly' && (
                          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                              <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={settings.emailNotifications.manager.days[day as keyof typeof settings.emailNotifications.manager.days]}
                                  onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    emailNotifications: {
                                      ...prev.emailNotifications,
                                      manager: {
                                        ...prev.emailNotifications.manager,
                                        days: {
                                          ...prev.emailNotifications.manager.days,
                                          [day]: e.target.checked
                                        }
                                      }
                                    }
                                  }))}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{day}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>CC Emails</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Please enter comma(,) separated email addresses for multiple CC emails."
                            value={settings.emailNotifications.manager.ccEmails}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                manager: { ...prev.emailNotifications.manager, ccEmails: e.target.value }
                              }
                            }))}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Notes</label>
                          <textarea
                            className="form-input"
                            rows={3}
                            placeholder="This note will be sent as body of email notification."
                            value={settings.emailNotifications.manager.notes}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                manager: { ...prev.emailNotifications.manager, notes: e.target.value }
                              }
                            }))}
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      {/* Trade Users */}
                      <div style={{ marginBottom: '32px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                          Trade Users
                        </h3>

                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="trade-frequency"
                              checked={settings.emailNotifications.trade.frequency === 'none'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  trade: { ...prev.emailNotifications.trade, frequency: 'none' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>None</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="trade-frequency"
                              checked={settings.emailNotifications.trade.frequency === 'daily'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  trade: { ...prev.emailNotifications.trade, frequency: 'daily' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Daily</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="radio"
                              name="trade-frequency"
                              checked={settings.emailNotifications.trade.frequency === 'weekly'}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  trade: { ...prev.emailNotifications.trade, frequency: 'weekly' }
                                }
                              }))}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>Weekly</span>
                          </label>

                          {settings.emailNotifications.trade.frequency !== 'none' && (
                            <select
                              value={settings.emailNotifications.trade.time}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  trade: { ...prev.emailNotifications.trade, time: e.target.value }
                                }
                              }))}
                              className="form-input"
                              style={{ width: '120px' }}
                            >
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, '0');
                                return <option key={hour} value={`${hour}:00`}>{`${hour}:00`}</option>;
                              })}
                            </select>
                          )}
                        </div>

                        {settings.emailNotifications.trade.frequency === 'weekly' && (
                          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                              <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={settings.emailNotifications.trade.days[day as keyof typeof settings.emailNotifications.trade.days]}
                                  onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    emailNotifications: {
                                      ...prev.emailNotifications,
                                      trade: {
                                        ...prev.emailNotifications.trade,
                                        days: {
                                          ...prev.emailNotifications.trade.days,
                                          [day]: e.target.checked
                                        }
                                      }
                                    }
                                  }))}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{day}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>CC Emails</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Please enter comma(,) separated email addresses for multiple CC emails."
                            value={settings.emailNotifications.trade.ccEmails}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                trade: { ...prev.emailNotifications.trade, ccEmails: e.target.value }
                              }
                            }))}
                          />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Notes</label>
                          <textarea
                            className="form-input"
                            rows={3}
                            placeholder="This note will be sent as body of email notification."
                            value={settings.emailNotifications.trade.notes}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              emailNotifications: {
                                ...prev.emailNotifications,
                                trade: { ...prev.emailNotifications.trade, notes: e.target.value }
                              }
                            }))}
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      {/* Save Button */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={() => handleSave()}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}
        </div>

        {/* Location Modal */}
        {showLocationModal && (
          <LocationModal
            location={editingLocation}
            projectName={projects.find(p => p._id === selectedProjectId)?.name || ''}
            nextOrder={locations.length + 1}
            onSave={handleSaveLocation}
            onCancel={() => {
              setShowLocationModal(false);
              setEditingLocation(null);
            }}
          />
        )}

        {showTagModal && (
          <SimpleModal
            item={editingTag}
            title={editingTag ? 'Edit Tag' : 'Add Tag'}
            onSave={handleSaveTag}
            onCancel={() => {
              setShowTagModal(false);
              setEditingTag(null);
            }}
            showDeficiencyPlace={true}
          />
        )}

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setLocationToDelete(null);
          }}
          onConfirm={confirmDeleteLocation}
          title="Delete Location"
          message="Are you sure you want to delete this location?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        <ConfirmDialog
          isOpen={showDeleteTagConfirm}
          onClose={() => {
            setShowDeleteTagConfirm(false);
            setTagToDelete(null);
          }}
          onConfirm={confirmDeleteTag}
          title="Delete Tag"
          message="Are you sure you want to delete this tag?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        {/* Deficiency Type Modal */}
        {showDeficiencyTypeModal && (
          <SimpleModal
            item={editingDeficiencyType}
            title={editingDeficiencyType ? 'Edit Deficiency Type' : 'Add Deficiency Type'}
            onSave={handleSaveDeficiencyType}
            onCancel={() => {
              setShowDeficiencyTypeModal(false);
              setEditingDeficiencyType(null);
            }}
            showDeficiencyPlace={true}
          />
        )}

        <ConfirmDialog
          isOpen={showDeleteDeficiencyTypeConfirm}
          onClose={() => {
            setShowDeleteDeficiencyTypeConfirm(false);
            setDeficiencyTypeToDelete(null);
          }}
          onConfirm={confirmDeleteDeficiencyType}
          title="Delete Deficiency Type"
          message="Are you sure you want to delete this deficiency type?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        {/* Priority Modal */}
        {showPriorityModal && (
          <SimpleModal
            item={editingPriority}
            title={editingPriority ? 'Edit Priority' : 'Add Priority'}
            onSave={handleSavePriority}
            onCancel={() => {
              setShowPriorityModal(false);
              setEditingPriority(null);
            }}
          />
        )}

        <ConfirmDialog
          isOpen={showDeletePriorityConfirm}
          onClose={() => {
            setShowDeletePriorityConfirm(false);
            setPriorityToDelete(null);
          }}
          onConfirm={confirmDeletePriority}
          title="Delete Priority"
          message="Are you sure you want to delete this priority?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        {/* Trade Modal */}
        {showTradeModal && (
          <SimpleModal
            item={editingTrade}
            title={editingTrade ? 'Edit Trade' : 'Add Trade'}
            onSave={handleSaveTrade}
            onCancel={() => {
              setShowTradeModal(false);
              setEditingTrade(null);
            }}
          />
        )}

        <ConfirmDialog
          isOpen={showDeleteTradeConfirm}
          onClose={() => {
            setShowDeleteTradeConfirm(false);
            setTradeToDelete(null);
          }}
          onConfirm={confirmDeleteTrade}
          title="Delete Trade"
          message="Are you sure you want to delete this trade?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        {/* Inspection Type Modal */}
        {showInspectionTypeModal && (
          <InspectionTypeModal
            item={editingInspectionType}
            projectName={projects.find(p => p._id === selectedProjectId)?.name || ''}
            onSave={handleSaveInspectionType}
            onCancel={() => {
              setShowInspectionTypeModal(false);
              setEditingInspectionType(null);
            }}
          />
        )}

        <ConfirmDialog
          isOpen={showDeleteInspectionTypeConfirm}
          onClose={() => {
            setShowDeleteInspectionTypeConfirm(false);
            setInspectionTypeToDelete(null);
          }}
          onConfirm={confirmDeleteInspectionType}
          title="Delete Inspection Type"
          message="Are you sure you want to delete this inspection type?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />

        {/* Add Users Modal */}
        {showAddUsersModal && (
          <AddUsersModal
            contacts={contacts}
            existingUsers={users}
            projectName={projects.find(p => p._id === selectedProjectId)?.name || ''}
            searchQuery={contactsSearch}
            onSearchChange={setContactsSearch}
            pagination={contactsPagination}
            onPageChange={(page) => setContactsPagination(prev => ({ ...prev, page }))}
            onSave={handleSaveUsers}
            onCancel={() => {
              setShowAddUsersModal(false);
              setContactsSearch('');
              setContactsPagination({ page: 1, limit: 20, total: 0, pages: 0 });
            }}
          />
        )}

        {/* Edit User Modal */}
        {showEditUserModal && editingUser && (
          <EditUserModal
            user={editingUser}
            projectName={projects.find(p => p._id === selectedProjectId)?.name || ''}
            trades={trades}
            onSave={handleSaveEditUser}
            onCancel={() => {
              setShowEditUserModal(false);
              setEditingUser(null);
            }}
          />
        )}

        {/* Delete User Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteUserConfirm}
          onClose={() => {
            setShowDeleteUserConfirm(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDeleteUser}
          title="Delete User"
          message="Are you sure you want to remove this user from the project?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />
      </>
    </AppLayout>
  );
};

// Location Modal Component
interface LocationModalProps {
  location: IssueLocation | null;
  projectName: string;
  nextOrder: number;
  onSave: (data: Omit<IssueLocation, 'id' | 'order'>) => void;
  onCancel: () => void;
}

const LocationModal: React.FC<LocationModalProps> = ({ location, projectName, nextOrder, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: location?.name || '',
    isSuiteUnit: location?.isSuiteUnit || false,
    isCommonArea: location?.isCommonArea || false
  });

  const [errors, setErrors] = useState({ name: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>{location ? 'Edit Location' : 'Add Location'}</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Project Name */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                Project: <span style={{ fontWeight: '600' }}>{projectName}</span>
              </label>
            </div>

            {/* Order */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                Order:
              </label>
              <input
                type="text"
                className="form-input"
                value={location?.order || nextOrder}
                disabled
                style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
              />
            </div>

            {/* Name */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                Name <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  setErrors({ name: '' });
                }}
              />
              {errors.name && <span style={{ color: '#DC2626', fontSize: '13px' }}>{errors.name}</span>}
            </div>

            {/* Deficiency Place */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                Deficiency Place <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isSuiteUnit}
                    onChange={(e) => setFormData({ ...formData, isSuiteUnit: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px' }}>Suite/Unit</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isCommonArea}
                    onChange={(e) => setFormData({ ...formData, isCommonArea: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px' }}>Common Area</span>
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="modal-footer" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-md"
              >
                {location ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Inspection Type Modal Component
interface InspectionTypeModalProps {
  item: ListItem | null;
  projectName: string;
  onSave: (data: { name: string; construction: boolean; inspector: boolean; manager: boolean; trade: boolean; showOnPurchaserPortal: boolean }) => void;
  onCancel: () => void;
}

const InspectionTypeModal: React.FC<InspectionTypeModalProps> = ({ item, projectName, onSave, onCancel }) => {
  const [name, setName] = useState(item?.name || '');
  const [construction, setConstruction] = useState(item?.construction || false);
  const [inspector, setInspector] = useState(item?.inspector || false);
  const [manager, setManager] = useState(item?.manager || false);
  const [trade, setTrade] = useState(item?.trade || false);
  const [showOnPurchaserPortal, setShowOnPurchaserPortal] = useState(item?.showOnPurchaserPortal || false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!construction && !inspector && !manager && !trade) {
      setError('Please select at least one role');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({ name: name.trim(), construction, inspector, manager, trade, showOnPurchaserPortal });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '400px',
          padding: '24px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
          {item ? 'Edit Inspection Type' : 'Add Inspection Type'}
        </h2>
        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>
          Project: {projectName}
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label className="form-label">
              Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              placeholder="Name"
              autoFocus
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label className="form-label">
              Roles <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginTop: '8px'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={construction}
                  onChange={(e) => {
                    setConstruction(e.target.checked);
                    setError('');
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Construction</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={inspector}
                  onChange={(e) => {
                    setInspector(e.target.checked);
                    setError('');
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Inspector</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={manager}
                  onChange={(e) => {
                    setManager(e.target.checked);
                    setError('');
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Manager</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={trade}
                  onChange={(e) => {
                    setTrade(e.target.checked);
                    setError('');
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Trade</span>
              </label>
            </div>
          </div>

          <div style={{
            marginBottom: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #E5E7EB'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showOnPurchaserPortal}
                onChange={(e) => setShowOnPurchaserPortal(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '500' }}>Show on purchaser portal</span>
            </label>
          </div>

          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary btn-md"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              disabled={isSubmitting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isSubmitting && (
                <div className="builders-loading-spinner" style={{
                  width: '16px',
                  height: '16px',
                  borderWidth: '2px'
                }} />
              )}
              {isSubmitting ? (item ? 'Updating...' : 'Adding...') : (item ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Simple Modal Component for Tags, Deficiency Types, Priorities, and Trades
interface SimpleModalProps {
  item: ListItem | null;
  title: string;
  onSave: (data: { name: string; isSuiteUnit: boolean; isCommonArea: boolean }) => void;
  onCancel: () => void;
  showDeficiencyPlace?: boolean;
}

const SimpleModal: React.FC<SimpleModalProps> = ({ item, title, onSave, onCancel, showDeficiencyPlace = false }) => {
  const [name, setName] = useState(item?.name || '');
  const [isSuiteUnit, setIsSuiteUnit] = useState(item?.isSuiteUnit || false);
  const [isCommonArea, setIsCommonArea] = useState(item?.isCommonArea || false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (showDeficiencyPlace && !isSuiteUnit && !isCommonArea) {
      setError('Please select at least one deficiency place');
      return;
    }

    onSave({ name: name.trim(), isSuiteUnit, isCommonArea });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Name */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                Name <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                autoFocus
              />
              {error && <span style={{ color: '#DC2626', fontSize: '13px' }}>{error}</span>}
            </div>

            {/* Deficiency Place - Only show for Tags and Deficiency Types */}
            {showDeficiencyPlace && (
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Deficiency Place <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isSuiteUnit}
                      onChange={(e) => {
                        setIsSuiteUnit(e.target.checked);
                        setError('');
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px' }}>Suite/Unit</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isCommonArea}
                      onChange={(e) => {
                        setIsCommonArea(e.target.checked);
                        setError('');
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px' }}>Common Area</span>
                  </label>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="modal-footer" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-md"
              >
                {item ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Add Users Modal Component
interface AddUsersModalProps {
  contacts: Contact[];
  existingUsers: User[];
  projectName: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  onPageChange: (page: number) => void;
  onSave: (selectedContacts: Array<{ contactId: string; name: string; email: string; construction: boolean; inspector: boolean; manager: boolean; trade: boolean }>) => void;
  onCancel: () => void;
}

const AddUsersModal: React.FC<AddUsersModalProps> = ({ contacts, existingUsers, projectName, searchQuery, onSearchChange, pagination, onPageChange, onSave, onCancel }) => {
  const [selectedContacts, setSelectedContacts] = useState<Map<string, { name: string; email: string; construction: boolean; inspector: boolean; manager: boolean; trade: boolean }>>(new Map());

  const existingContactIds = new Set(existingUsers.map(u => u.contactId));
  const availableContacts = contacts.filter(c => !existingContactIds.has(c._id));

  const handleToggleContact = (contact: Contact) => {
    const newSelected = new Map(selectedContacts);
    const primaryEmail = contact.emails.find(e => e.isPrimary)?.email || contact.emails[0]?.email || '';

    if (newSelected.has(contact._id)) {
      newSelected.delete(contact._id);
    } else {
      newSelected.set(contact._id, {
        name: contact.displayName,
        email: primaryEmail,
        construction: false,
        inspector: false,
        manager: false,
        trade: false
      });
    }
    setSelectedContacts(newSelected);
  };

  const handleToggleRole = (contactId: string, role: 'construction' | 'inspector' | 'manager' | 'trade') => {
    const newSelected = new Map(selectedContacts);
    const contact = newSelected.get(contactId);
    if (contact) {
      contact[role] = !contact[role];
      newSelected.set(contactId, contact);
      setSelectedContacts(newSelected);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contactsToAdd = Array.from(selectedContacts.entries()).map(([contactId, data]) => ({
      contactId,
      ...data
    }));
    onSave(contactsToAdd);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>Add Users</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Search */}
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>

            {/* Contacts Table */}
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
              <table className="data-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}></th>
                    <th>Name</th>
                    <th>Email</th>
                    <th style={{ textAlign: 'center' }}>Construction</th>
                    <th style={{ textAlign: 'center' }}>Inspector</th>
                    <th style={{ textAlign: 'center' }}>Manager</th>
                    <th style={{ textAlign: 'center' }}>Trade</th>
                  </tr>
                </thead>
                <tbody>
                  {availableContacts.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        {searchQuery ? 'No contacts found matching your search.' : 'No contacts available to add.'}
                      </td>
                    </tr>
                  ) : (
                    availableContacts.map((contact) => {
                      const isSelected = selectedContacts.has(contact._id);
                      const contactData = selectedContacts.get(contact._id);
                      const primaryEmail = contact.emails.find(e => e.isPrimary)?.email || contact.emails[0]?.email || '';

                      return (
                        <tr key={contact._id} style={{ backgroundColor: isSelected ? '#FEF3C7' : 'transparent' }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleContact(contact)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </td>
                          <td>{contact.displayName}</td>
                          <td>{primaryEmail}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={contactData?.construction || false}
                              onChange={() => handleToggleRole(contact._id, 'construction')}
                              disabled={!isSelected}
                              style={{ width: '18px', height: '18px', cursor: isSelected ? 'pointer' : 'not-allowed' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={contactData?.inspector || false}
                              onChange={() => handleToggleRole(contact._id, 'inspector')}
                              disabled={!isSelected}
                              style={{ width: '18px', height: '18px', cursor: isSelected ? 'pointer' : 'not-allowed' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={contactData?.manager || false}
                              onChange={() => handleToggleRole(contact._id, 'manager')}
                              disabled={!isSelected}
                              style={{ width: '18px', height: '18px', cursor: isSelected ? 'pointer' : 'not-allowed' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={contactData?.trade || false}
                              onChange={() => handleToggleRole(contact._id, 'trade')}
                              disabled={!isSelected}
                              style={{ width: '18px', height: '18px', cursor: isSelected ? 'pointer' : 'not-allowed' }}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => onPageChange(1)}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    background: pagination.page === 1 ? '#F3F4F6' : '#FFFFFF',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  First
                </button>
                {Array.from({ length: Math.min(9, pagination.pages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => onPageChange(page)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        background: pagination.page === page ? '#8B6F5C' : '#FFFFFF',
                        color: pagination.page === page ? '#FFFFFF' : '#000000',
                        cursor: 'pointer'
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => onPageChange(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    background: pagination.page === pagination.pages ? '#F3F4F6' : '#FFFFFF',
                    cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Last
                </button>
              </div>
            )}

            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
              Page Size: {pagination.limit} | {pagination.total} Record(s) Found
            </p>

            {/* Buttons */}
            <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-md"
                disabled={selectedContacts.size === 0}
              >
                Add
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit User Modal Component
interface EditUserModalProps {
  user: User;
  projectName: string;
  trades: ListItem[];
  onSave: (data: { construction: boolean; inspector: boolean; manager: boolean; trade: boolean; tradeIds: string[] }) => void;
  onCancel: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, projectName, trades, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    construction: user.construction,
    inspector: user.inspector,
    manager: user.manager,
    trade: user.trade,
    tradeIds: user.tradeIds || []
  });
  const [tradeSearch, setTradeSearch] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleTradeToggle = (tradeId: string) => {
    setFormData(prev => {
      const newTradeIds = prev.tradeIds.includes(tradeId)
        ? prev.tradeIds.filter(id => id !== tradeId)
        : [...prev.tradeIds, tradeId];
      return { ...prev, tradeIds: newTradeIds };
    });
  };

  const filteredTrades = trades.filter(trade =>
    trade.name.toLowerCase().includes(tradeSearch.toLowerCase())
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>Edit User</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                <strong>Project Name:</strong> {projectName}
              </p>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                <strong>Name:</strong> {user.name}
              </p>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                <strong>Email:</strong> {user.email}
              </p>
            </div>

            {/* Role Checkboxes */}
            <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.construction}
                  onChange={(e) => setFormData({ ...formData, construction: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Construction</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.inspector}
                  onChange={(e) => setFormData({ ...formData, inspector: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Inspector</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Manager</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.trade}
                  onChange={(e) => {
                    const newTradeValue = e.target.checked;
                    setFormData({
                      ...formData,
                      trade: newTradeValue,
                      tradeIds: newTradeValue ? formData.tradeIds : []
                    });
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Trade</span>
              </label>
            </div>

            {/* Trades Selection - Only show when Trade is checked */}
            {formData.trade && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Select Trades ({formData.tradeIds.length} selected)
                </label>

                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search trades..."
                  value={tradeSearch}
                  onChange={(e) => setTradeSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '14px'
                  }}
                />

                {/* Trades List */}
                <div
                  style={{
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    maxHeight: '250px',
                    overflowY: 'auto',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  {trades.length === 0 ? (
                    <div style={{ padding: '16px', color: '#6B7280', textAlign: 'center' }}>
                      No trades available
                    </div>
                  ) : filteredTrades.length === 0 ? (
                    <div style={{ padding: '16px', color: '#6B7280', textAlign: 'center' }}>
                      No trades match your search
                    </div>
                  ) : (
                    filteredTrades.map(trade => (
                      <label
                        key={trade.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '12px 14px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #F3F4F6',
                          backgroundColor: formData.tradeIds.includes(trade.id) ? '#FEF3C7' : 'transparent',
                          transition: 'background-color 0.15s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.tradeIds.includes(trade.id)}
                          onChange={() => handleTradeToggle(trade.id)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px' }}>{trade.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-md"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceSettings;
